import { Post, PostData } from "../types/socialJournal";
import { apiClient, uploadClient } from "./api";
export interface Comment {
  comment_id: number | string;
  post_id?: number | string;
  author_id?: number;
  parent_comment_id?: number | string;
  parentComment?: number | string | null;
  content: string;
  created_at: string;
  updated_at?: string;
  author?: {
    id?: number;
    user_id?: number;
    username?: string;
    first_name?: string;
    last_name?: string;
    avatar?: string | null;
    // Label and avatar fallback when the author has no name.
    email?: string;
    phone?: string;
  };
  reply_count: number;
  post?: number | string;
  replies?: Comment[];
  reactions?: { [emoji: string]: number };
  user_reaction?: string | null;
  userHasLiked?: boolean;
}
// ✅ REMOVED: All transformation functions (no longer needed)
// Backend returns data in the exact format frontend expects
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: Pagination;
}
/**
 * Fetch feed posts
 * ✅ UPDATED: No transformation needed - backend returns frontend-ready data
 */
export const fetchFeed = async (
  page = 1,
  limit = 20,
  filter:
    | "Me"
    | "followers"
    | "both"
    | "one_follower"
    | ""
    | undefined = "both",
  profile_id?: number,
): Promise<PaginatedResponse<Post>> => {
  try {
    const params: any = { filter, page, limit };
    if (profile_id) {
      params.profile_id = profile_id;
    }
    const response = await apiClient.get("/api/reflections/feed", { params });
    // ✅ Backend already returns data in correct format - use directly
    return response.data;
  } catch (error) {
    console.error("Error fetching feed:", error);
    throw error;
  }
};
export const fetchReplies = async (
  commentId: number,
  postId: number,
): Promise<Comment[]> => {
  try {
    const response = await apiClient.get(`/api/comments/${commentId}/replies`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching replies:", error);
    throw error;
  }
};
/**
 * Fetch single post by ID
 * ✅ UPDATED: No transformation needed
 */
export const fetchPostById = async (postId: string): Promise<Post> => {
  try {
    const response = await apiClient.get(`/api/posts/${postId}`);

    // ✅ Backend returns data in correct format
    return response.data.data;
  } catch (error) {
    console.error("Error fetching post:", error);
    throw error;
  }
};

/**
 * Create a new post
 * ✅ Keep transformation for sending data TO backend
 */
export interface CreatePostData {
  description?: string;
  template_type: "plain" | "template" | "image" | "mixed";
  visibility: "followers" | "private";
  feelings?: string;
  images?: string[];
  therapy_data?: any;
  therapy_frequency?: string;
}

export const createPost = async (postData: CreatePostData): Promise<Post> => {
  try {
    const response = await apiClient.post("/api/posts/", postData);

    // ✅ Backend returns created post in correct format
    return response.data.data;
  } catch (error) {
    console.error("Error creating post:", error);
    throw error;
  }
};

/**
 * Update an existing post
 */
export const updatePost = async (
  postId: string,
  postData: Partial<CreatePostData>,
): Promise<Post> => {
  try {
    const response = await apiClient.put(`/api/posts/${postId}`, postData);

    // ✅ Backend returns updated post in correct format
    return response.data.data;
  } catch (error) {
    console.error("Error updating post:", error);
    throw error;
  }
};

/**
 * Delete a post
 */
export const deletePost = async (postId: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/posts/${postId}`);
  } catch (error) {
    console.error("Error deleting post:", error);
    throw error;
  }
};

/**
 * React to a post
 * ✅ UPDATED: Send reaction as-is (no mapping needed)
 */
export const reactToPost = async (
  postId: string,
  reaction: string,
): Promise<void> => {
  try {
    await apiClient.post(`/api/posts/${postId}/react`, {
      emoji_code: reaction, // Backend expects 'emoji_code'
    });
  } catch (error) {
    console.error("Error reacting to post:", error);
    throw error;
  }
};

/**
 * Remove reaction from a post
 */
export const removeReaction = async (postId: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/posts/${postId}/react`);
  } catch (error) {
    console.error("Error removing reaction:", error);
    throw error;
  }
};

/**
 * Add comment to a post
 */
export interface CreateCommentData {
  content: string;
  parent_comment_id?: number;
}

export const addComment = async (
  postId: string,
  commentData: CreateCommentData,
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `/api/posts/${postId}/comments`,
      commentData,
    );
    return response.data.data;
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

/**
 * Fetch comments for a post
 */
export const fetchComments = async (postId: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/api/posts/${postId}/comments`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching comments:", error);
    throw error;
  }
};

/**
 * Helper function to build create post data
 * This is the ONLY transformation - from frontend form to backend format
 */
export const buildCreatePostData = (
  content: string,
  feeling: string,
  images: string[] = [],
  visibility: "followers" | "private" = "followers",
  therapyData?: any,
  therapyFrequency?: string,
): CreatePostData => {
  const templateType =
    images.length > 0 ? (content ? "mixed" : "image") : "plain";

  return {
    description: content,
    template_type: templateType,
    visibility,
    feelings: feeling,
    images,
    therapy_data: therapyData,
    therapy_frequency: therapyFrequency,
  };
};
export interface BackendUploadResponse {
  url: string;
  name: string;
  [key: string]: any;
}

export const uploadImagesBackendFetch = async (
  files: File[],
): Promise<BackendUploadResponse[]> => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("images", file, file.name);
    });

    // Was a raw fetch with a hand-built Authorization header and NO refresh path (see
    // tokenStore). uploadClient already has the shared-refresh interceptor.
    const response = await uploadClient.post(`/api/posts/upload/backend`, formData);

    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return responseData.data.images;
    }

    throw new Error(responseData.message || "Failed to upload images");
  } catch (error: any) {
    console.error("Fetch upload error:", error);
    throw new Error("Failed to upload images via fetch");
  }
};
export const fetchReactions = async (
  postId: number,
): Promise<{ [emoji: string]: number }> => {
  try {
    const response = await apiClient.get(`/api/posts/${postId}/reactions`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    return {};
  } catch (error) {
    console.error("Error fetching reactions:", error);
    return {};
  }
};
export const addCommentReaction = async (
  postID: number,
  emojiCode: string,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await apiClient.post(`/api/posts/${postID}/react`, {
      emoji_code: emojiCode,
    });

    if (response.data.success) {
      return { success: true, message: response.data.message };
    }

    throw new Error(response.data.message || "Failed to add comment reaction");
  } catch (error: any) {
    console.error("Error adding comment reaction:", error);
    throw new Error(
      error.response?.data?.message || "Failed to add comment reaction",
    );
  }
};
export const toggleCommentReaction = async (
  commentId: number,
  emojiCode: string,
): Promise<{
  action: "added" | "removed";
  reactions?: { [emoji: string]: number };
}> => {
  try {
    const response = await apiClient.post(`/api/comments/${commentId}/react`, {
      emoji_code: emojiCode,
    });

    if (response.data.success) {
      return {
        action: response.data.action,
        reactions: response.data.reactions,
      };
    }

    throw new Error(
      response.data.message || "Failed to toggle comment reaction",
    );
  } catch (error: any) {
    console.error("Error toggling comment reaction:", error);
    throw new Error(
      error.response?.data?.message || "Failed to toggle comment reaction",
    );
  }
};
export const deleteComment = async (
  commentId: number,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await apiClient.delete(`/api/comments/${commentId}`);
    if (response.data.success) {
      return { success: true, message: response.data.message };
    }
    throw new Error(response.data.message || "Failed to delete comment");
  } catch (error: any) {
    console.error("Error deleting comment:", error);
    throw new Error(
      error.response?.data?.message || "Failed to delete comment",
    );
  }
};

export const editComment = async (
  commentId: number | string,
  content: string,
): Promise<Comment> => {
  try {
    const response = await apiClient.put(`/api/comments/${commentId}`, {
      content,
    });
    return response.data.data as Comment;
  } catch (error: any) {
    console.error("Error editing comment:", error);
    throw new Error(error.response?.data?.message || "Failed to edit comment");
  }
};

export const changePostPrivacy = async (
  postId: string,
  visibility: "private" | "followers",
  postData: PostData,
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await apiClient.put(`/api/posts/${postId}`, {
      ...postData,
      therapy_data: postData.therapyData,
      visibility,
    });
    if (response.data.success) {
      return { success: true, message: response.data.message };
    }
    throw new Error(response.data.message || "Failed to change privacy");
  } catch (error: any) {
    console.error("Error changing privacy:", error);
    throw new Error(
      error.response?.data?.message || "Failed to change privacy",
    );
  }
};
export const fetchCommentReplies = async (
  postId: number,
  page = 1,
  limit = 20,
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `/api/posts/${postId}/comments?page=${page}&limit=${limit}`,
    );
    if (response.data.success) {
      return response.data;
    }
    throw new Error(response.data.message || "Failed to fetch replies");
  } catch (error: any) {
    console.error("Error fetching replies:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch replies");
  }
};

// Optionally export Post type for use elsewhere
export interface UploadRequestResponse {
  uploadUrl: string;
  uploadId: string;
  expiresAt: number;
}

export interface VerifyUploadResponse {
  uploadHash: string;
  url: string;
  s3Path: string;
  fileSize: number;
  contentType: string;
}

// 1️⃣ Request upload URL
export const requestUploadUrl = async (
  fileName: string,
  contentType: string,
  fileSize: number,
): Promise<UploadRequestResponse> => {
  const response = await apiClient.post("/api/posts/upload/request", {
    fileName,
    contentType,
    fileSize,
  });
  return response.data.data;
};

// 2️⃣ Upload file to S3
export const uploadToS3 = async (
  presignedUrl: string,
  file: File,
): Promise<void> => {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
      "x-amz-server-side-encryption": "AES256",
    },
  });
  if (!response.ok) throw new Error("S3 upload failed");
};

// 3️⃣ Verify upload
export const verifyUpload = async (
  uploadId: string,
): Promise<VerifyUploadResponse> => {
  const response = await apiClient.post(`/api/posts/upload/verify/${uploadId}`);
  return response.data.data;
};

// 4️⃣ Complete upload flow for one image
export const uploadImagePresigned = async (
  file: File,
): Promise<VerifyUploadResponse> => {
  const req = await requestUploadUrl(file.name, file.type, file.size);
  await uploadToS3(req.uploadUrl, file);
  return await verifyUpload(req.uploadId);
};

export type { Post };
